import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined'
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import type { CreateUserRequest, RoleDTO, RoleEnum, UpdateUserRequest, UsuarioDTO, UsuarioStatus, WrapperResponse } from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import { clip, onlyLetters } from '../../../utils/inputs'
import { errorMessage, STATUS_LABELS, statusTone, USERS_PATH } from '../common/user-common'
import type { FormMessage, UsersLocationState } from '../common/user-common'

export interface UserFormState {
    id: number | null
    status: UsuarioStatus | null
    correo: string
    nombres: string
    apPat: string
    apMat: string
    celular: string
}

export interface RoleNode {
    role: RoleEnum
    children?: RoleEnum[]
}

export interface RoleGroup {
    id: string
    title: string
    Icon: typeof ListAltOutlinedIcon
    tone: string
    roles: RoleNode[]
}

export interface PrivilegePermission {
    id: number
    name: string
    description: string
}

/** Nodo del árbol de privilegios: las categorías agrupan y solo los roles se seleccionan. */
export type PrivilegeNode =
    | {
        kind: 'category'
        id: string
        label: string
        Icon: typeof ListAltOutlinedIcon
        tone: string
        selected: number
        total: number
        children: PrivilegeNode[]
    }
    | {
        kind: 'role'
        id: RoleEnum
        label: string
        description: string
        permisos: PrivilegePermission[]
        children: PrivilegeNode[]
    }

const CATEGORY_PREFIX = 'categoria:'

/** Mismo orden y jerarquía que la pantalla original de privilegios. */
export const ROLE_GROUPS: RoleGroup[] = [
    {
        id: 'catalogos',
        title: 'Catálogos',
        Icon: ListAltOutlinedIcon,
        tone: 'bg-tone-purple text-tone-purple-ink',
        roles: [
            { role: 'ROLE_CLIENTES', children: ['ROLE_CLIENTES_R', 'ROLE_CLIENTES_W', 'ROLE_CLIENTES_D'] },
            { role: 'ROLE_PROSPECTOS', children: ['ROLE_PROSPECTOS_R', 'ROLE_PROSPECTOS_W', 'ROLE_PROSPECTOS_D'] },
            { role: 'ROLE_VENDEDORES', children: ['ROLE_VENDEDORES_R', 'ROLE_VENDEDORES_W', 'ROLE_VENDEDORES_D'] },
        ],
    },
    {
        id: 'modulos',
        title: 'Módulos',
        Icon: ExtensionOutlinedIcon,
        tone: 'bg-tone-teal text-tone-teal-ink',
        roles: [
            { role: 'ROLE_POLIZAS', children: ['ROLE_POLIZAS_R', 'ROLE_POLIZAS_W', 'ROLE_POLIZAS_D', 'ROLE_POLIZAS_RENOVACIONES', 'ROLE_POLIZAS_COBRANZA', 'ROLE_POLIZAS_IMPORT'] },
            { role: 'ROLE_SINIESTROS', children: ['ROLE_SINIESTROS_R', 'ROLE_SINIESTROS_W', 'ROLE_SINIESTROS_D'] },
            { role: 'ROLE_COTIZACIONES', children: ['ROLE_COTIZACIONES_R', 'ROLE_COTIZACIONES_W', 'ROLE_COTIZACIONES_D', 'ROLE_COTIZACIONES_TEMP'] },
            { role: 'ROLE_CALENDARIO' },
            { role: 'ROLE_TAREAS' },
            { role: 'ROLE_PROSPECCION', children: ['ROLE_PROSPECCION_R', 'ROLE_PROSPECCION_W', 'ROLE_PROSPECCION_D'] },
        ],
    },
    {
        id: 'reportes',
        title: 'Reportes',
        Icon: AssessmentOutlinedIcon,
        tone: 'bg-tone-orange text-tone-orange-ink',
        roles: [
            { role: 'ROLE_REPORTES', children: ['ROLE_REPORTES_POLIZAS', 'ROLE_REPORTES_RECIBOS', 'ROLE_REPORTES_CLIENTES', 'ROLE_REPORTES_COMISIONES', 'ROLE_REPORTES_AUTOMATIZACIONES', 'ROLE_REPORTE_SEMANAL'] },
        ],
    },
    {
        id: 'administracion',
        title: 'Administración',
        Icon: TuneOutlinedIcon,
        tone: 'bg-tone-pink text-tone-pink-ink',
        roles: [
            { role: 'ROLE_ADMIN' },
            { role: 'ROLE_USUARIOS' },
            { role: 'ROLE_FACTURACION' },
            { role: 'ROLE_EMPRESA' },
            { role: 'ROLE_CONFIG' },
            { role: 'ROLE_INTEGRACIONES' },
            { role: 'ROLE_CLAVES' },
            { role: 'ROLE_ASEGURADORAS' },
            { role: 'ROLE_COMUNICADOS' },
            { role: 'ROLE_METAS' },
        ],
    },
]

const LOAD_ERROR = 'Error al cargar la página'
const SAVE_ERROR = 'Error al guardar el usuario'

const emptyState: UserFormState = {
    id: null,
    status: null,
    correo: '',
    nombres: '',
    apPat: '',
    apMat: '',
    celular: '',
}

const toFormState = (body: UsuarioDTO): UserFormState => ({
    id: body.id,
    status: body.status,
    correo: body.correo || '',
    nombres: body.nombres || '',
    apPat: body.apPat || '',
    apMat: body.apMat || '',
    celular: body.celular || '',
})

const toRoleNode = (dto: RoleDTO | undefined, role: RoleEnum, children: PrivilegeNode[] = []): PrivilegeNode | null => {
    if (!dto) return null
    return {
        kind: 'role',
        id: role,
        label: dto.nombre || role,
        description: dto.descripcion || '',
        permisos: (dto.permisos || []).map((permiso) => ({
            id: permiso.id,
            name: permiso.nombre || permiso.permiso,
            description: permiso.descripcion || '',
        })),
        children,
    }
}

const roleIds = (nodes: PrivilegeNode[]): RoleEnum[] => nodes.flatMap((node) => [
    ...(node.kind === 'role' ? [node.id] : []),
    ...roleIds(node.children),
])

const parentIds = (nodes: PrivilegeNode[]): string[] => nodes.flatMap((node) => [
    ...(node.children.length > 0 ? [node.id] : []),
    ...parentIds(node.children),
])

export const isCategory = (node: PrivilegeNode) => node.kind === 'category'

export const useUserFormModel = () => {
    const history = useHistory()
    const location = useLocation()
    const { usuarioId } = useParams()
    const backUrl: string = (location.state as UsersLocationState | null)?.backUrl || USERS_PATH

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [message, setMessage] = useState<FormMessage | null>(null)
    const [catalog, setCatalog] = useState<RoleDTO[]>([])
    const [selected, setSelected] = useState<Set<RoleEnum>>(new Set())
    const [state, setState] = useState<UserFormState>(emptyState)

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                const rolesResponse: WrapperResponse<RoleDTO[]> | null = await (await APIInvoke.invokeGET('/seguridad/roles')).json()
                if (!active) return
                if (!rolesResponse?.ok) throw new Error(rolesResponse?.message || LOAD_ERROR)
                setCatalog(rolesResponse.body || [])
                if (!usuarioId) return
                const userResponse: WrapperResponse<UsuarioDTO> | null = await (await APIInvoke.invokeGET(`/usuarios/${usuarioId}`)).json()
                if (!active) return
                if (!userResponse?.ok || !userResponse.body) throw new Error(userResponse?.message || LOAD_ERROR)
                setState(toFormState(userResponse.body))
                setSelected(new Set(userResponse.body.roles || []))
            } catch (error) {
                console.error(LOAD_ERROR, error)
                if (active) setMessage({ type: 'error', message: errorMessage(error, LOAD_ERROR) })
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [usuarioId])

    const [expanded, setExpanded] = useState<string[]>([])

    const baseTree = useMemo<PrivilegeNode[]>(() => {
        const byRole = new Map(catalog.map((dto) => [dto.role, dto]))
        return ROLE_GROUPS.flatMap((group): PrivilegeNode[] => {
            const children = group.roles.flatMap((node) => {
                const nested = (node.children || [])
                    .map((child) => toRoleNode(byRole.get(child), child))
                    .filter((child): child is PrivilegeNode => child !== null)
                const roleNode = toRoleNode(byRole.get(node.role), node.role, nested)
                return roleNode ? [roleNode] : []
            })
            if (children.length === 0) return []
            return [{
                kind: 'category',
                id: `${CATEGORY_PREFIX}${group.id}`,
                label: group.title,
                Icon: group.Icon,
                tone: group.tone,
                selected: 0,
                total: roleIds(children).length,
                children,
            }]
        })
    }, [catalog])

    const treeRoleIds = useMemo(() => new Set(roleIds(baseTree)), [baseTree])

    const privilegeTree = useMemo<PrivilegeNode[]>(() => baseTree.map((node) => (
        node.kind === 'category'
            ? { ...node, selected: roleIds(node.children).filter((role) => selected.has(role)).length }
            : node
    )), [baseTree, selected])

    useEffect(() => {
        setExpanded(parentIds(baseTree))
    }, [baseTree])

    const selectedTreeIds = useMemo(() => [...selected].filter((role) => treeRoleIds.has(role)), [selected, treeRoleIds])

    const toggleRole = (role: RoleEnum) => setSelected((current) => {
        if (!treeRoleIds.has(role)) return current
        const next = new Set(current)
        if (next.has(role)) next.delete(role)
        else next.add(role)
        return next
    })

    const changeTreeSelection = (ids: string[]) => setSelected((current) => {
        const outsideTree = [...current].filter((role) => !treeRoleIds.has(role))
        const fromTree = ids.filter((id) => treeRoleIds.has(id))
        return new Set([...outsideTree, ...fromTree])
    })

    const patch = (partial: Partial<UserFormState>) => setState((current) => ({ ...current, ...partial }))

    const change = {
        correo: (value: string) => patch({ correo: clip(value.trim(), 100) }),
        nombres: (value: string) => patch({ nombres: onlyLetters(value, 100) }),
        apPat: (value: string) => patch({ apPat: onlyLetters(value, 60) }),
        apMat: (value: string) => patch({ apMat: onlyLetters(value, 60) }),
        celular: (value: string) => patch({ celular: clip(value, 15) }),
    }

    const save = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        try {
            setSaving(true)
            setMessage(null)
            const roles = [...selected]
            const response: WrapperResponse<UsuarioDTO> = state.id
                ? await (await APIInvoke.invokePUT('/usuarios', {
                    id: state.id,
                    nombres: state.nombres,
                    apPat: state.apPat,
                    apMat: state.apMat,
                    celular: state.celular,
                    roles,
                } satisfies UpdateUserRequest)).json()
                : await (await APIInvoke.invokePOST('/usuarios', {
                    nombres: state.nombres,
                    apPat: state.apPat,
                    apMat: state.apMat,
                    celular: state.celular,
                    correo: state.correo,
                    roles,
                } satisfies CreateUserRequest)).json()
            if (!response.ok) {
                setMessage({ type: 'error', message: response.message || SAVE_ERROR })
                return
            }
            history.push({ pathname: USERS_PATH, state: { flash: response.message || 'Usuario guardado correctamente' } satisfies UsersLocationState })
        } catch (error) {
            console.error(error)
            setMessage({ type: 'error', message: SAVE_ERROR })
        } finally {
            setSaving(false)
        }
    }

    return {
        form: state,
        privilegeTree,
        selectedTreeIds,
        expanded,
        loading,
        saving,
        busy: loading || saving,
        busyLabel: loading ? 'Cargando usuario…' : 'Guardando usuario…',
        message,
        isEdit: Boolean(state.id),
        title: state.id ? 'Editar usuario' : 'Nuevo usuario',
        status: state.status ? { label: STATUS_LABELS[state.status], tone: statusTone(state.status) } : null,
        selectedCount: selected.size,
        backTo: backUrl,
        change,
        changeTreeSelection,
        toggleRole,
        changeExpanded: setExpanded,
        save,
        cancel: () => history.push(backUrl),
    }
}
